// dSpr Email Logs - `/dSpr_EmailLogs`
//
// Admin-only audit trail of every email dispatched by the dSpr sender.
// Read-only - every row here was written by persistDispatchLog() in
// dSpr_EmailSender.js at send time.
//
// Phase 1 (this file): 4 stat cards / filter row / paged table / detail
//   drawer / Excel + PDF exports.
// Phase 2 (separate work): SendGrid event webhook updates the row's
//   status/last_event/last_event_at fields after delivery confirmation.
//   This file already renders those fields - they just show "-" until the
//   webhook ships.
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
// Swal removed - themed alerts via useDsprAlerts() (see utils/dSprAlerts.js).
import { useDsprAlerts } from "./utils/dSprAlerts";
import { fmtDateTimeIST, istTodayISODate } from "./utils/dSpr_DateFormat";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
// jspdf-autotable v5 changed from monkey-patching `doc.autoTable(opts)` to a
// standalone function `autoTable(doc, opts)`. The `import "jspdf-autotable"`
// side-effect import no longer attaches the method - must import the
// default export and call it directly. Calling `doc.autoTable(...)` under
// v5 throws "doc.autoTable is not a function" (the bug HR hit on prod).
import autoTable from "jspdf-autotable";

// ── Template key → friendly label + accent color ────────────────────────────
// Supports BOTH the legacy `self_*` keys (current sender call sites) and the
// DB-canonical `request_review` / `reminder_N` keys (post-refactor naming).
const TEMPLATE_META = {
    self_review_request:    { label: "Request review",     bg: "#d1fae5", fg: "#065f46" },
    request_review:         { label: "Request review",     bg: "#d1fae5", fg: "#065f46" },
    self_reminder_1:        { label: "Reminder 1",         bg: "#fef3c7", fg: "#92400e" },
    reminder_1:             { label: "Reminder 1",         bg: "#fef3c7", fg: "#92400e" },
    self_reminder_2:        { label: "Reminder 2",         bg: "#ede9fe", fg: "#5b21b6" },
    reminder_2:             { label: "Reminder 2",         bg: "#ede9fe", fg: "#5b21b6" },
    self_confirmation:      { label: "Confirmation",       bg: "#dbeafe", fg: "#1e40af" },
    confirmation:           { label: "Confirmation",       bg: "#dbeafe", fg: "#1e40af" },
    confirmation_pdf:       { label: "Confirmation (PDF)", bg: "#ede9fe", fg: "#5b21b6" },
    submission_admin_notify:{ label: "Admin notify",       bg: "#f1f5f9", fg: "#475569" },
    reminder_hit_admin:     { label: "Reminder hit admin", bg: "#fef3c7", fg: "#92400e" },
};
function templateMeta(key) {
    return TEMPLATE_META[key] || { label: key || "-", bg: "#f1f5f9", fg: "#475569" };
}

// ── Status enum → display label + color ─────────────────────────────────────
const STATUS_META = {
    queued:      { label: "Queued",     bg: "#e0e7ff", fg: "#3730a3" },
    sent:        { label: "Sent",       bg: "#dcfce7", fg: "#15803d" },  // legacy
    delivered:   { label: "Delivered",  bg: "#dcfce7", fg: "#15803d" },
    bounced:     { label: "Bounced",    bg: "#fee2e2", fg: "#b91c1c" },
    dropped:     { label: "Dropped",    bg: "#fee2e2", fg: "#b91c1c" },
    blocked:     { label: "Blocked",    bg: "#fee2e2", fg: "#b91c1c" },
    spam_report: { label: "Spam",       bg: "#fee2e2", fg: "#b91c1c" },
    failed:      { label: "Failed",     bg: "#fee2e2", fg: "#b91c1c" },
    deferred:    { label: "Deferred",   bg: "#fef3c7", fg: "#92400e" },
};
function statusMeta(s) {
    return STATUS_META[s] || { label: s || "-", bg: "#f1f5f9", fg: "#475569" };
}
const STATUS_OPTIONS = [
    { value: "",            label: "Any" },
    { value: "queued",      label: "Queued" },
    { value: "delivered",   label: "Delivered" },
    { value: "bounced",     label: "Bounced" },
    { value: "dropped",     label: "Dropped" },
    { value: "deferred",    label: "Deferred" },
    { value: "blocked",     label: "Blocked" },
    { value: "spam_report", label: "Spam" },
    { value: "failed",      label: "Failed" },
    { value: "sent",        label: "Sent (legacy)" },
];

// Formatters live in src/utils/dSpr_DateFormat.js - imported above.

function DSprEmailLogs({ navSize }) {
    const navigate = useNavigate();
    const { showAlert, AlertsHost } = useDsprAlerts();

    // ── State ───────────────────────────────────────────────────────────────
    const [filters, setFilters] = useState({
        from:      istTodayISODate(30),
        to:        istTodayISODate(0),
        template:  "",
        recipient: "",
        status:    "",
    });
    // Applied filters - only updated when "Apply" is clicked. Decouples typing
    // from fetching (HR can type a date without firing 5 requests).
    const [appliedFilters, setAppliedFilters] = useState(filters);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [loading, setLoading] = useState(true);

    const [stats, setStats] = useState({
        today:    { total: 0, failed: 0 },
        week:     { total: 0, failed: 0 },
        month:    { total: 0, failed: 0 },
        all_time: { total: 0, failed: 0 },
    });
    const [templateKeys, setTemplateKeys] = useState([]);

    const [selectedRow, setSelectedRow] = useState(null); // drawer payload (or null)
    const [drawerLoading, setDrawerLoading] = useState(false);

    const [exporting, setExporting] = useState(null); // "excel" | "pdf" | null

    // ── Auth gate ──────────────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/employee/logined_employee", { credentials: "include" });
                const data = await res.json();
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login"); return;
                }
                const me = Array.isArray(data) ? data[0] : data;
                if (!me?.emp_id) { navigate("/login"); return; }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            } catch { navigate("/login"); return; }
            // First load
            await Promise.all([fetchTemplateKeys(), fetchStats()]);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTemplateKeys = async () => {
        try {
            const res = await apiFetch("/api/dspr/email-logs/template-keys", { credentials: "include" });
            const list = await res.json();
            setTemplateKeys(Array.isArray(list) ? list : []);
        } catch { /* fine - dropdown shows just "All" */ }
    };

    const fetchStats = async () => {
        try {
            const res = await apiFetch("/api/dspr/email-logs/stats", { credentials: "include" });
            const data = await res.json();
            if (res.ok) setStats(data);
        } catch { /* tolerated */ }
    };

    // Build query string from applied filters + page
    const buildQuery = useCallback((extra = {}) => {
        const q = new URLSearchParams({
            from:      appliedFilters.from,
            to:        appliedFilters.to,
            page:      String(page),
            pageSize:  String(pageSize),
        });
        if (appliedFilters.template)  q.set("template", appliedFilters.template);
        if (appliedFilters.recipient) q.set("recipient", appliedFilters.recipient);
        if (appliedFilters.status)    q.set("status", appliedFilters.status);
        Object.entries(extra).forEach(([k, v]) => q.set(k, v));
        return q.toString();
    }, [appliedFilters, page, pageSize]);

    // Fingerprint of the last successfully-fetched payload. Silent polls
    // compare against this and skip the state update when nothing changed,
    // so React doesn't re-render the table on every no-op tick. JSON.stringify
    // over the rows array + total is cheap enough at the page size we paginate
    // to (50 rows) - no need for a real hash function.
    const lastFingerprint = useRef("");

    const fetchRows = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const res = await apiFetch(`/api/dspr/email-logs?${buildQuery()}`, { credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't load email logs");
            const nextRows  = Array.isArray(data.rows) ? data.rows : [];
            const nextTotal = data.total || 0;
            // Hash-dedupe: skip the setState pair on identical payloads so
            // the table doesn't re-render every 15s when nothing's changed.
            // Whitelisted fields only - any back-end-side field that might
            // change (status, last_event, last_event_at, delivered_at) is
            // included; volatile fields the UI doesn't display are skipped.
            const fingerprint = JSON.stringify({
                t: nextTotal,
                r: nextRows.map(r => [r.id, r.status, r.last_event, r.last_event_at, r.delivered_at]),
            });
            if (fingerprint === lastFingerprint.current && silent) return;
            lastFingerprint.current = fingerprint;
            setRows(nextRows);
            setTotal(nextTotal);
        } catch (e) {
            // Only surface the alert on user-initiated fetches. Silent
            // background polling failing once shouldn't pop a toast - it'll
            // retry on the next tick anyway.
            if (!silent) {
                showAlert("error", "Couldn't load email logs", e.message);
                setRows([]); setTotal(0);
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [buildQuery]);

    // Fetch rows whenever applied filters or page change. Reset the
    // fingerprint so a filter change always re-renders (the dedupe should
    // never block a user-initiated fetch).
    useEffect(() => {
        lastFingerprint.current = "";
        fetchRows();
    }, [fetchRows]);

    // ── Live status polling ────────────────────────────────────────────────
    //
    // Steady 15s heartbeat that ALWAYS runs while the page is mounted, so:
    //   (a) status transitions (queued → delivered / bounced / etc.) appear
    //       within ~15s of SendGrid firing the webhook
    //   (b) NEW rows from background sends (cron reminders, manual sends in
    //       other tabs) appear without HR having to click Apply Filters -
    //       Apply Filters is purely a filter action, not a refresh
    //
    // Conditional skips for politeness:
    //   - document.hidden  → tab in background, don't burn requests
    //   - selectedRow      → drawer open, don't yank rows under the user
    //   - exporting        → mid-export, don't fight in-flight work
    //
    // Silent mode + hash-dedupe in fetchRows together mean a no-op tick
    // costs one HTTP round-trip and zero React re-renders. At ~10ms per
    // server-side query that's ~19s of DB time per viewer per 8h day -
    // negligible.
    useEffect(() => {
        if (selectedRow || exporting) return;
        const id = setInterval(() => {
            if (document.hidden) return;
            fetchRows({ silent: true });
            fetchStats();
        }, 15000);
        return () => clearInterval(id);
    }, [selectedRow, exporting, fetchRows]);

    // ── Filter actions ─────────────────────────────────────────────────────
    const applyFilters = () => {
        // Reset to page 1 whenever filters change
        setPage(1);
        setAppliedFilters(filters);
    };
    const clearFilters = () => {
        const defaults = {
            from:      istTodayISODate(30),
            to:        istTodayISODate(0),
            template:  "",
            recipient: "",
            status:    "",
        };
        setFilters(defaults);
        setAppliedFilters(defaults);
        setPage(1);
    };

    // ── Drawer ─────────────────────────────────────────────────────────────
    const openRow = async (rowId) => {
        setDrawerLoading(true);
        setSelectedRow({ id: rowId }); // open shell immediately
        try {
            const res = await apiFetch(`/api/dspr/email-logs/${rowId}`, { credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't load detail");
            setSelectedRow(data);
        } catch (e) {
            showAlert("error", "Couldn't load detail", e.message);
            setSelectedRow(null);
        } finally {
            setDrawerLoading(false);
        }
    };
    const closeDrawer = () => setSelectedRow(null);

    // Esc closes the drawer
    useEffect(() => {
        if (!selectedRow) return;
        const onKey = (e) => { if (e.key === "Escape") closeDrawer(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectedRow]);

    // ── Export helpers - fetch full rowset via ?all=true then write file ───
    const fetchAllRows = async () => {
        const res = await apiFetch(`/api/dspr/email-logs?${buildQuery({ all: "true" })}`, { credentials: "include" });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Export fetch failed");
        return data;
    };

    const exportExcel = async () => {
        setExporting("excel");
        try {
            const data = await fetchAllRows();
            const wb = new ExcelJS.Workbook();
            wb.creator = "Dolluz Corp · SPR Portal";
            wb.created = new Date();
            const ws = wb.addWorksheet("SPR Email Logs");

            // ── dEpr theme palette (ARGB, FF = full alpha) ─────────────
            // Matches src/backend_routes/dSpr_Reports_server.js per-emp
            // Excel + dSpr_GenerateReviewPdf.js. Same colours = same
            // brand across UI, PDF, Excel.
            const XL_NAVY   = "FF0D1B2A";
            const XL_ORANGE = "FFE8520A";
            const XL_SUB    = "FFAABBCC";
            const XL_SLATE  = "FF64748B";
            const XL_LIGHT  = "FFF8FAFC";
            const XL_LINE   = "FFE2E8F0";
            const XL_ZEBRA  = "FFFAFBFD";

            ws.columns = [
                { key: "sent_at",    width: 22 },
                { key: "template",   width: 22 },
                { key: "to",         width: 32 },
                { key: "subject",    width: 50 },
                { key: "status",     width: 14 },
                { key: "http",       width: 8  },
                { key: "sg_id",      width: 36 },
                { key: "last_event", width: 18 },
                { key: "error",      width: 40 },
            ];

            // ── Navy header band (rich text: white + orange period) ────
            const brandRow = ws.addRow([]);
            ws.mergeCells(brandRow.number, 1, brandRow.number, 9);
            const brandCell = brandRow.getCell(1);
            brandCell.value = {
                richText: [
                    { text: "Dolluz Corp",   font: { bold: true, color: { argb: "FFFFFFFF" }, size: 16, name: "Calibri" } },
                    { text: ".",             font: { bold: true, color: { argb: XL_ORANGE }, size: 16, name: "Calibri" } },
                    { text: "    SPR Portal",font: { bold: true, color: { argb: XL_ORANGE }, size: 16, name: "Calibri" } },
                ],
            };
            brandCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_NAVY } };
            brandCell.alignment = { vertical: "middle", indent: 1 };
            brandRow.height = 28;

            const subRow = ws.addRow(["Email Logs - Confidential audit trail"]);
            ws.mergeCells(subRow.number, 1, subRow.number, 9);
            const subCell = subRow.getCell(1);
            subCell.font = { color: { argb: XL_SUB }, size: 10, name: "Calibri" };
            subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_NAVY } };
            subCell.alignment = { vertical: "middle", indent: 1 };
            subRow.height = 18;

            ws.addRow([]);

            // ── Meta block (window + generated stamp) ──────────────────
            const metaRows = [
                ["Window:",     `${appliedFilters.from}  →  ${appliedFilters.to}`],
                ["Generated:",  fmtDateTimeIST(new Date().toISOString()) + " IST"],
                ["Rows:",       String(data.rows.length)],
            ];
            metaRows.forEach(([k, v]) => {
                const row = ws.addRow([k, v]);
                row.getCell(1).font = { color: { argb: XL_SLATE }, size: 10 };
                row.getCell(2).font = { bold: true, color: { argb: XL_NAVY }, size: 10 };
            });
            ws.addRow([]);

            // ── Table header - navy fill, white text, bold ─────────────
            const hdrRow = ws.addRow([
                "Sent At (IST)", "Template", "To", "Subject",
                "Status", "HTTP", "SG Message ID", "Last Event", "Error",
            ]);
            hdrRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10.5, name: "Calibri" };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_NAVY } };
                cell.alignment = { vertical: "middle", indent: 1 };
                cell.border = { bottom: { style: "thin", color: { argb: XL_LINE } } };
            });
            hdrRow.height = 22;

            // ── Body rows with zebra striping ──────────────────────────
            data.rows.forEach((r, i) => {
                const row = ws.addRow({
                    sent_at:    fmtDateTimeIST(r.created_at),
                    template:   templateMeta(r.template_key).label,
                    to:         r.to_email || "",
                    subject:    r.subject || "",
                    status:     statusMeta(r.status).label,
                    http:       r.http_status ?? "",
                    sg_id:      r.sg_message_id || "",
                    last_event: r.last_event || "",
                    error:      r.error_message || "",
                });
                row.eachCell((cell, col) => {
                    cell.font = { color: { argb: XL_NAVY }, size: 10, name: "Calibri" };
                    if (i % 2 === 1) {
                        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_ZEBRA } };
                    }
                    cell.alignment = { vertical: "top", wrapText: col === 4 || col === 9 };
                    cell.border = { bottom: { style: "hair", color: { argb: XL_LINE } } };
                });
                // Status cell - orange accent so failures jump out
                const statusVal = (r.status || "").toLowerCase();
                if (["bounced", "dropped", "blocked", "spam_report", "failed"].includes(statusVal)) {
                    row.getCell("status").font = { bold: true, color: { argb: "FFB91C1C" }, size: 10 };
                } else if (statusVal === "delivered") {
                    row.getCell("status").font = { bold: true, color: { argb: "FF15803D" }, size: 10 };
                } else if (statusVal === "queued" || statusVal === "deferred") {
                    row.getCell("status").font = { bold: true, color: { argb: XL_ORANGE }, size: 10 };
                }
            });

            const buf = await wb.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `dspr-email-logs-${appliedFilters.from}-to-${appliedFilters.to}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            if (data.truncated) {
                showAlert("warning",
                    `Exported first ${data.pageSize.toLocaleString()} rows`,
                    "Tighten the date range or template filter to capture older entries in a follow-up export.");
            } else {
                showAlert("success", `Exported ${data.rows.length} rows`);
            }
        } catch (e) {
            showAlert("error", "Excel export failed", e.message);
        } finally {
            setExporting(null);
        }
    };

    const exportPDF = async () => {
        setExporting("pdf");
        try {
            const data = await fetchAllRows();
            const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });

            // ── dEpr-style PDF theme ───────────────────────────────────────
            // Mirrors src/utils/dSpr_GenerateReviewPdf.js + the per-emp
            // Excel export: navy header band with "Dolluz Corp." (orange
            // period) + tiny orange "SPR Portal" tag, slate meta block
            // under it, orange-on-light table head. Same RGB triplets as
            // the rest of the dSpr surfaces so the audit paper trail
            // visually matches the UI.
            const NAVY        = [13, 27, 42];
            const ORANGE      = [232, 82, 10];
            const HEADER_SUB  = [170, 187, 204];
            const SLATE       = [100, 116, 139];
            const ZEBRA       = [248, 250, 252];
            const pageWidth   = doc.internal.pageSize.getWidth();

            // Navy header band - 60pt tall, full-width
            doc.setFillColor(NAVY[0], NAVY[1], NAVY[2]);
            doc.rect(0, 0, pageWidth, 60, "F");
            // "Dolluz Corp" white + "." orange + "  SPR Portal" orange
            doc.setFont("helvetica", "bold");
            doc.setFontSize(18);
            doc.setTextColor(255, 255, 255);
            doc.text("Dolluz Corp", 28, 32);
            const wDol = doc.getTextWidth("Dolluz Corp");
            doc.setTextColor(ORANGE[0], ORANGE[1], ORANGE[2]);
            doc.text(".", 28 + wDol, 32);
            const wDot = doc.getTextWidth(".");
            doc.text("    SPR Portal", 28 + wDol + wDot, 32);
            // Sub-line on the navy band
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(HEADER_SUB[0], HEADER_SUB[1], HEADER_SUB[2]);
            doc.text("Email Logs - Confidential audit trail", 28, 48);

            // Title under the band
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
            doc.text(`Email Logs · ${appliedFilters.from} to ${appliedFilters.to}`, 28, 84);

            // Meta line
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(SLATE[0], SLATE[1], SLATE[2]);
            doc.text(
                `Generated: ${fmtDateTimeIST(new Date().toISOString())}    ·    ${data.rows.length} row${data.rows.length === 1 ? "" : "s"}`,
                28, 100
            );

            autoTable(doc, {
                startY: 116,
                head: [["Sent At (IST)", "Template", "To", "Subject", "Status", "HTTP", "Last Event"]],
                body: data.rows.map(r => [
                    fmtDateTimeIST(r.created_at),
                    templateMeta(r.template_key).label,
                    r.to_email || "",
                    (r.subject || "").substring(0, 70),
                    statusMeta(r.status).label,
                    r.http_status ?? "-",
                    r.last_event || "-",
                ]),
                styles: { fontSize: 8.5, cellPadding: 5, textColor: [13, 27, 42], lineColor: [226, 232, 240], lineWidth: 0.5 },
                headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold", fontSize: 9 },
                alternateRowStyles: { fillColor: ZEBRA },
                columnStyles: {
                    0: { cellWidth: 95 },
                    1: { cellWidth: 70 },
                    2: { cellWidth: 130 },
                    3: { cellWidth: "auto" },
                    4: { cellWidth: 65 },
                    5: { cellWidth: 38, halign: "right" },
                    6: { cellWidth: 70 },
                },
                didDrawPage: (hookData) => {
                    // Page footer - slim grey line
                    const ph = doc.internal.pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.setTextColor(SLATE[0], SLATE[1], SLATE[2]);
                    doc.text(
                        `Dolluz Corp · SPR Portal · Page ${hookData.pageNumber}`,
                        28, ph - 18
                    );
                },
            });
            doc.save(`dspr-email-logs-${appliedFilters.from}-to-${appliedFilters.to}.pdf`);
            if (data.truncated) {
                showAlert("warning",
                    `Exported first ${data.pageSize.toLocaleString()} rows`,
                    "Tighten the date range or template filter to capture older entries in a follow-up export.");
            } else {
                showAlert("success", `Exported ${data.rows.length} rows`);
            }
        } catch (e) {
            showAlert("error", "PDF export failed", e.message);
        } finally {
            setExporting(null);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const endRow = Math.min(page * pageSize, total);

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                {/* Mounted INSIDE the dashboard so the banner doesn't become
                    a flex-row item next to the dashboard (which caused the
                    horizontal jump). */}
                <AlertsHost />
                {/* ── Page header with [⟲ live] pill (Add 3) ──────────────── */}
                {/* No dspr-elog-region here — that class adds a white card +
                    dashed border, which clashes with how the rest of the
                    dAdmin section renders its headings (Support_Tickets style
                    sits directly on the grey page background). The region
                    treatment is still applied to the body blocks below. */}
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        SPR - Email Logs
                        <span className="dspr-elog-live-pill"
                            title="Page reflects the latest dispatch log writes. Refresh to re-pull. SendGrid lifecycle events update in real time once configured.">
                            ⟲ live
                        </span>
                    </h2>
                    <p className="dspr-subtitle">
                        Every email dispatched by SPR - sends, failures, full audit trail.
                    </p>
                </div>

                <div className="dspr-dashboard-content">
                    {/* ── Region 1 - 4 stat cards ─────────────────────────── */}
                    <div className="dspr-elog-region dspr-elog-stats-grid">
                        <StatCard label="TODAY"        data={stats.today} />
                        <StatCard label="LAST 7 DAYS"  data={stats.week} />
                        <StatCard label="LAST 30 DAYS" data={stats.month} />
                        <StatCard label="ALL TIME"     data={stats.all_time} />
                    </div>

                    {/* ── Region 2 - Filter row (fixed grid matching dEpr) ─────
                        Layout spec: FROM 140 · TO 140 · TEMPLATE 180 ·
                        RECIPIENT flex:1 (min 180) · STATUS 120 · action cluster.
                        flexWrap:nowrap so the row never collapses into two lines -
                        RECIPIENT shrinks to absorb leftover space instead of pushing
                        buttons off. */}
                    <div className="dspr-elog-region dspr-elog-filters">
                        <div className="dspr-elog-filter-row" style={{
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 12,
                            flexWrap: "nowrap",
                        }}>
                            <div className="dspr-elog-field" style={{ width: 140, flexShrink: 0 }}>
                                <label>FROM</label>
                                <input type="date" value={filters.from}
                                    onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
                                    onClick={e => e.target.showPicker?.()} />
                            </div>
                            <div className="dspr-elog-field" style={{ width: 140, flexShrink: 0 }}>
                                <label>TO</label>
                                <input type="date" value={filters.to}
                                    onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
                                    onClick={e => e.target.showPicker?.()} />
                            </div>
                            <div className="dspr-elog-field" style={{ width: 180, flexShrink: 0 }}>
                                <label>TEMPLATE</label>
                                <select value={filters.template}
                                    onChange={e => setFilters(f => ({ ...f, template: e.target.value }))}>
                                    <option value="">All templates</option>
                                    {templateKeys.map(k => (
                                        <option key={k} value={k}>{templateMeta(k).label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="dspr-elog-field" style={{ flex: 1, minWidth: 180 }}>
                                <label>RECIPIENT</label>
                                <input type="text" placeholder="Email contains..."
                                    value={filters.recipient}
                                    onChange={e => setFilters(f => ({ ...f, recipient: e.target.value }))}
                                    onKeyDown={e => { if (e.key === "Enter") applyFilters(); }} />
                            </div>
                            <div className="dspr-elog-field" style={{ width: 120, flexShrink: 0 }}>
                                <label>STATUS</label>
                                <select value={filters.status}
                                    onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                                    {STATUS_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Action cluster - right-aligned, fixed widths so the
                                whole group sits tight against the right edge. */}
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                                <button type="button" className="dspr-elog-btn-link" onClick={clearFilters}>Clear</button>
                                <button type="button" className="dspr-elog-btn-primary" onClick={applyFilters}>Apply filters</button>
                                <button type="button" className="dspr-elog-btn-excel"
                                    onClick={exportExcel}
                                    disabled={!!exporting || loading}>
                                    {exporting === "excel" ? "Exporting…" : "↓ Excel"}
                                </button>
                                <button type="button" className="dspr-elog-btn-pdf"
                                    onClick={exportPDF}
                                    disabled={!!exporting || loading}>
                                    {exporting === "pdf" ? "Exporting…" : "↓ PDF"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Region 3 - Logs table ───────────────────────────── */}
                    <div className="dspr-elog-region dspr-elog-table-wrap">
                        <div className="dspr-elog-table-header">
                            <span>Showing <strong>{startRow}</strong>-<strong>{endRow}</strong> of <strong>{total.toLocaleString()}</strong> logs</span>
                            {loading && <span className="dspr-elog-loading-tag">Loading…</span>}
                        </div>
                        <table className="dspr-elog-table">
                            <thead>
                                <tr>
                                    <th>Sent At</th>
                                    <th>Template</th>
                                    <th>To</th>
                                    <th>Subject</th>
                                    <th>Status</th>
                                    <th>HTTP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loading && rows.length === 0 ? (
                                    <tr><td colSpan={6} className="dspr-empty">No email logs match the current filters.</td></tr>
                                ) : rows.map(r => {
                                    const t = templateMeta(r.template_key);
                                    const s = statusMeta(r.status);
                                    return (
                                        <tr key={r.id} className="dspr-elog-row" onClick={() => openRow(r.id)}>
                                            <td className="dspr-elog-cell-when">{fmtDateTimeIST(r.created_at)}</td>
                                            <td><span className="dspr-elog-pill" style={{ background: t.bg, color: t.fg }}>{t.label}</span></td>
                                            <td className="dspr-elog-cell-trunc" title={r.to_email || ""}>{r.to_email || "-"}</td>
                                            <td className="dspr-elog-cell-trunc" title={r.subject || ""}>{r.subject || "-"}</td>
                                            <td><span className="dspr-elog-pill" style={{ background: s.bg, color: s.fg }}>{s.label}</span></td>
                                            <td className="dspr-elog-cell-http">{r.http_status ?? "-"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="dspr-elog-pagination">
                                <button type="button" disabled={page <= 1} onClick={() => setPage(1)}>« First</button>
                                <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</button>
                                <span className="dspr-elog-page-info">Page <strong>{page}</strong> of <strong>{totalPages}</strong></span>
                                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next ›</button>
                                <button type="button" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>Last »</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Region 4 - Detail drawer ────────────────────────────────── */}
            {selectedRow && (
                <LogDetailDrawer
                    row={selectedRow}
                    loading={drawerLoading}
                    onClose={closeDrawer}
                />
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// STAT CARD - count + failed sub-text + progress bar
// ════════════════════════════════════════════════════════════════════════════
function StatCard({ label, data }) {
    const total = data?.total || 0;
    const failed = data?.failed || 0;
    const okCount = total - failed;
    const failPct = total > 0 ? Math.round((failed / total) * 100) : 0;
    const okPct = total > 0 ? 100 - failPct : 0;
    return (
        <div className="dspr-elog-stat">
            <div className="dspr-elog-stat-label">{label}</div>
            <div className="dspr-elog-stat-num">{total.toLocaleString()}</div>
            <div className="dspr-elog-stat-sub">
                {failed === 0
                    ? "0 failed"
                    : `${failed} failed (${failPct}%)`}
            </div>
            <div className="dspr-elog-stat-bar">
                <div className="dspr-elog-stat-bar-ok" style={{ width: `${okPct}%` }} />
                <div className="dspr-elog-stat-bar-fail" style={{ width: `${failPct}%` }} />
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// DETAIL DRAWER - slide-in from right, fresh fetch per :id
// ════════════════════════════════════════════════════════════════════════════
function LogDetailDrawer({ row, loading, onClose }) {
    const t = templateMeta(row.template_key);
    const s = statusMeta(row.status);
    return (
        <>
            <div className="dspr-elog-drawer-backdrop" />
            <div className="dspr-elog-drawer">
                <div className="dspr-elog-drawer-head">
                    <span>Email Log Detail</span>
                    <button className="dspr-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="dspr-elog-drawer-body">
                    {loading && !row.template_key ? (
                        <div className="dspr-empty">Loading…</div>
                    ) : (
                        <>
                            <DetailRow label="Template" value={
                                <span className="dspr-elog-pill" style={{ background: t.bg, color: t.fg }}>{t.label}</span>
                            } />
                            <DetailRow label="Status" value={
                                <span className="dspr-elog-pill" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
                            } />
                            <DetailRow label="HTTP" value={row.http_status ?? "-"} />
                            <DetailRow label="SG Message ID" value={row.sg_message_id || "-"} mono />
                            <DetailRow label="Last SG Event" value={
                                row.last_event
                                    ? `${row.last_event} · ${fmtDateTimeIST(row.last_event_at)}`
                                    : "-"
                            } />
                            <DetailRow label="Delivered At" value="-" />{/* Phase 2 - column added by webhook migration */}
                            <DetailRow label="To" value={row.to_email || "-"} />
                            <DetailRow label="CC" value={row.cc_email || "-"} />
                            <DetailRow label="BCC" value={row.bcc_email || "-"} />
                            <DetailRow label="Subject" value={row.subject || "-"} block />
                            <DetailRow label="Sent At" value={fmtDateTimeIST(row.created_at)} />
                            <DetailRow label="Attachments" value="0" />
                            {row.error_message && (
                                <DetailRow label="Error Message" value={
                                    <span className="dspr-elog-error-text">{row.error_message}</span>
                                } block />
                            )}
                            {row.preview && (
                                <DetailRow label="Preview" value={
                                    <pre className="dspr-elog-preview-pre">{row.preview}</pre>
                                } block />
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

function DetailRow({ label, value, mono, block }) {
    return (
        <div className={`dspr-elog-detail-row ${block ? "block" : ""}`}>
            <div className="dspr-elog-detail-label">{label}</div>
            <div className={`dspr-elog-detail-value ${mono ? "mono" : ""}`}>{value}</div>
        </div>
    );
}

export default DSprEmailLogs;
